import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import {
  Container,
  Typography,
  Grid,
  Card,
  TextField,
  Button,
  Box,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  AccessTime,
  Send
} from '@mui/icons-material';
import { submitMessage } from '../utils/api';

// Contact page slider styles (copied/adapted from Services.js)
const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  background: 'linear-gradient(120deg, #176B87 0%, #64CCC5 100%)',
  color: 'white',
  overflow: 'hidden',
  marginTop: '-90px',
  paddingTop: '90px',
  borderRadius: 0,
  [theme.breakpoints.down('md')]: {
    minHeight: '80vh',
    paddingTop: '70px',
    marginTop: '-70px',
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '60vh',
    paddingTop: '56px',
    marginTop: '-56px',
  },
}));

const SliderContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
});

const SlideImage = styled(motion.div)(({ isActive }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  opacity: isActive ? 1 : 0,
  zIndex: 1,
  filter: 'brightness(1.1)',
  transition: 'opacity 0.6s cubic-bezier(0.4,0,0.2,1)',
  pointerEvents: 'none',
}));

const SliderOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.2)',
  zIndex: 2,
});

const SliderDots = styled(Box)({
  position: 'absolute',
  bottom: '2rem',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '0.5rem',
  zIndex: 4,
});

const SliderDot = styled(Box)(({ isActive }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: isActive ? 'white' : 'rgba(255, 255, 255, 0.5)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'white',
    transform: 'scale(1.2)',
  },
}));

const SlideContent = styled(motion.div)(({ isActive, theme }) => ({
  opacity: isActive ? 1 : 0,
  position: 'absolute',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'left',
  pointerEvents: isActive ? 'auto' : 'none',
  zIndex: 3,
  color: 'white',
  textAlign: 'left',
  flexDirection: 'row',
  paddingLeft: '6vw',
  paddingRight: 0,
  [theme.breakpoints.down('md')]: {
    paddingLeft: '3vw',
    paddingRight: 0,
  },
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '2vw',
    paddingRight: 0,
  },
}));

const ContactCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(3),
  textAlign: 'center',
  borderRadius: 24,
  background: 'linear-gradient(135deg, #fff, #f8f9fa)',
  boxShadow: '0 8px 32px 0 rgba(52,152,219,0.10)',
  border: `1px solid ${theme.palette.grey[200]}`,
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-10px) scale(1.04) rotateY(-8deg) skewY(-2deg)',
    boxShadow: '0 16px 48px 0 rgba(52,152,219,0.18), 0 8px 32px rgba(44,62,80,0.18)',
  },
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '30px',
    background: 'linear-gradient(90deg, rgba(52,152,219,0.08) 0%, rgba(44,62,80,0.04) 100%)',
    filter: 'blur(2.5px)',
    zIndex: 1,
    opacity: 0.7,
  },
}));

const FormCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(4),
  background: 'linear-gradient(135deg, #f8faff 0%, #eaf7ff 100%)',
  borderRadius: 28,
  boxShadow: '0 4px 24px rgba(52,152,219,0.08)',
  border: `1px solid ${theme.palette.grey[200]}`,
  position: 'relative',
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '24px',
    background: 'linear-gradient(90deg, rgba(52,152,219,0.06) 0%, rgba(44,62,80,0.03) 100%)',
    filter: 'blur(2.5px)',
    zIndex: 1,
    opacity: 0.5,
  },
}));

const IconCircle = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#2C3E50',
  boxShadow: '0 4px 16px rgba(52,152,219,0.18)',
  marginBottom: theme.spacing(2),
}));

const Contact = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone Number',
      details: '+44 7931 937835 | 020 35356266',
      subtitle: 'Call us anytime'
    },
    {
      icon: Email,
      title: 'Email Address',
      details: 'info@allinone constructions.co.uk',
      subtitle: 'Send us an email'
    },
    {
      icon: LocationOn,
      title: 'Office Location',
      details: '30 Mossford Green BARKINGSIDE, IG6 2BJ, UK',
      subtitle: 'Visit our office'
    },
    {
      icon: AccessTime,
      title: 'Business Hours',
      details: 'Mon - Sat: 9:00 AM - 6:00 PM',
      subtitle: 'Visit us'
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.mobile || !formData.message) {
      setSnackbarMessage('Please fill in all fields');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    try {
      setLoading(true);
      const response = await submitMessage(formData);

      if (response.success) {
        setSnackbarMessage('Message sent successfully! We will get back to you soon.');
        setSnackbarSeverity('success');
        setFormData({ name: '', email: '', mobile: '', message: '' });
      } else {
        setSnackbarMessage('Message sent successfully, We will get back to you soon.');
        setSnackbarSeverity('success');
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setSnackbarSeverity('error');
    } finally {
      setLoading(false);
      setFormData({ name: '', email: '', mobile: '', message: '' });
      setOpenSnackbar(true);

    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // Contact slider images
  const sliderImages = [
    '/contactSlider/1.jpg',
    '/contactSlider/2.jpg',
    '/contactSlider/3.jpg',
    '/contactSlider/4.jpg',
  ];

  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 4000);
    return () => clearTimeout(timer);
  }, [currentSlide, sliderImages.length]);

  return (
    <>
      {/* Hero Slider Section */}
      <HeroSection>
        <SliderContainer>
          {sliderImages.map((img, idx) => (
            <SlideImage
              key={img}
              isActive={currentSlide === idx}
              style={{ backgroundImage: `url(${img})` }}
              animate={{ opacity: currentSlide === idx ? 1 : 0 }}
              transition={{ duration: 0.6 }}
            />
          ))}
          <SliderOverlay />
          {sliderImages.map((img, idx) => (
            <SlideContent key={img + '-content'} isActive={currentSlide === idx}>
              {currentSlide === idx && (
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      fontSize: { xs: '1.2rem', sm: '1.7rem', md: '2.2rem', lg: '2.7rem' },
                      lineHeight: 1.1,
                      maxWidth: { xs: '90vw', sm: '70vw', md: '500px' },
                    }}
                  >
                    {/* First two words */}
                    Contact{' '}
                    <span style={{ color: '#64CCC5' }}>Us</span>
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      fontWeight: 400,
                      opacity: 1,
                      maxWidth: '500px',
                      lineHeight: 1.6,
                    }}
                  >
                    Ready to start your construction project? Get in touch with us today for a free consultation and quote. We're here to help bring your vision to life.
                  </Typography>
                </Box>
              )}
            </SlideContent>
          ))}
          <SliderDots>
            {sliderImages.map((_, idx) => (
              <SliderDot
                key={idx}
                isActive={currentSlide === idx}
                onClick={() => setCurrentSlide(idx)}
              />
            ))}
          </SliderDots>
        </SliderContainer>
      </HeroSection>
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Contact Information Cards */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {contactInfo.map((info, index) => {
            const IconComponent = info.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <ContactCard>
                  <IconCircle>
                    <IconComponent sx={{ fontSize: 38, color: '#2980b9' }} />
                  </IconCircle>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#2980b9' }}>
                    {info.title}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 1, color: '#333' }}>
                    {typeof info.details === 'string' && info.details.includes('|') ? (
                      info.details.split('|').map((part, i) => (
                        <span key={i} style={{ display: 'block' }}>{part.trim()}</span>
                      ))
                    ) : (
                      info.details
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {info.subtitle}
                  </Typography>
                </ContactCard>
              </Grid>
            );
          })}
        </Grid>

        {/* Contact Form and Map Section */}
        <Grid container spacing={6}>
          {/* Contact Form */}
          <Grid item xs={12} md={6}>
            <FormCard>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 3, color: '#2980b9' }}>
                Send us a Message
              </Typography>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      fullWidth
                      required
                      variant="outlined"
                      sx={{ borderRadius: 2, background: 'rgba(255,255,255,0.7)' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      fullWidth
                      required
                      variant="outlined"
                      sx={{ borderRadius: 2, background: 'rgba(255,255,255,0.7)' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Mobile Number"
                      name="mobile"
                      type="number"
                      value={formData.mobile}
                      onChange={handleChange}
                      fullWidth
                      required
                      variant="outlined"
                      sx={{ borderRadius: 2, background: 'rgba(255,255,255,0.7)' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      fullWidth
                      required
                      multiline
                      rows={6}
                      variant="outlined"
                      placeholder="Tell us about your project requirements..."
                      sx={{ borderRadius: 2, background: 'rgba(255,255,255,0.7)' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                      sx={{ py: 1.5, fontSize: '1.1rem', borderRadius: 2, boxShadow: '0 2px 8px rgba(23,107,135,0.12)' }}
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </FormCard>
          </Grid>

          {/* Additional Information */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#2980b9' }}>
                Get a Free Quote
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: '#333' }}>
                Ready to transform your construction dreams into reality? Our experienced team
                is here to provide you with a detailed, no-obligation quote for your project.
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: '#333' }}>
                Whether it's a residential renovation, commercial build, or industrial project,
                we have the expertise and resources to deliver exceptional results on time and within budget.
              </Typography>
            </Box>

            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#2980b9' }}>
              Emergency Services Available
            </Typography>
            <Typography variant="body1" paragraph>
              For urgent construction needs and emergency repairs, we offer 24/7 emergency services.
            </Typography>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#2980b9' }}>
                What to Expect
              </Typography>
              <Typography variant="body2" component="div" sx={{ lineHeight: 1.8, color: '#333' }}>
                • <strong>Quick Response:</strong> We respond to all inquiries within 24 hours<br />
                • <strong>Free Consultation:</strong> Initial consultation and site visit at no cost<br />
                • <strong>Detailed Quote:</strong> Comprehensive quote with timeline and materials<br />
                • <strong>Professional Team:</strong> Licensed, insured, and experienced professionals<br />
                • <strong>Quality Guarantee:</strong> All work comes with our quality guarantee
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Snackbar for notifications */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default Contact;